import { LitElement, html, nothing, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import {
  SVG_CLOSE,
  SVG_CHEVRON_LEFT,
  SVG_CHEVRON_RIGHT,
  SVG_DOWNLOAD,
} from '@getgo/chameleon-icons'
import { GalleryEvent, type PreviewLoadErrorEventDetail } from '@/models/gallery-event'
import { contentTypeToLabel } from '@/utils/content-type-label'
import { t } from '@/i18n/translate-utils'
import type { FileModel } from '@/models/gallery.model'
import { isSupportedImagePreview, renderPreviewIcon } from './gallery-preview-icon'
import styles from './gallery-details-view.styles.scss?inline'

type PreviewMode = 'image' | 'video' | 'audio' | 'highlighted-icon' | 'neutral-icon'
const PLAYABLE_MEDIA_TIMEOUT_MS = 30_000

function getPreviewMode(file: FileModel, hasPlayableMediaError: boolean): PreviewMode {
  if (!isSupportedImagePreview(file.contentType) || hasPlayableMediaError) {
    return 'highlighted-icon'
  }
  if (file.contentType.startsWith('image/') && file.thumbnailUrl) {
    return 'image'
  }
  if (file.contentType.startsWith('video/')) {
    return 'video'
  }
  if (file.contentType.startsWith('audio/')) {
    return 'audio'
  }
  return 'neutral-icon'
}

/**
 * Full-page detail view for a single file in the GoTo Gallery SPA.
 *
 * Shows a file preview (image or icon fallback), navigation controls to move
 * between files, a select checkbox, and a download button.
 *
 * Keyboard shortcuts:
 * - `Escape` emits `detail-close`.
 * - `ArrowLeft` emits `detail-prev`.
 * - `ArrowRight` emits `detail-next`.
 *
 * @prop file          - The file to display.
 * @prop index         - Zero-based index of the current file.
 * @prop total         - Total number of files in the gallery.
 * @prop isSelected    - Whether the current file is selected.
 * @prop selectedCount - Number of currently selected files.
 * @prop selectedSize  - Formatted total size of selected files.
 * @prop isDownloading - True while a download is in progress.
 *
 * @fires detail-close    - `CustomEvent<void>` when the close button is clicked or `Escape` is pressed.
 * @fires detail-prev     - `CustomEvent<void>` when the previous button is clicked or `ArrowLeft` is pressed.
 * @fires detail-next     - `CustomEvent<void>` when the next button is clicked or `ArrowRight` is pressed.
 * @fires file-select     - `CustomEvent<string>` with the file ID when the select checkbox changes.
 * @fires download-request - `CustomEvent<void>` when the download button is clicked.
 */
export class GalleryDetailsView extends LitElement {
  static readonly tagName = 'gallery-details-view'
  static styles = unsafeCSS(styles)

  /** The file to display. If null the component renders nothing. */
  @property({ attribute: false }) file: FileModel | null = null

  /** Zero-based index of the current file in the gallery. */
  @property({ type: Number }) index = 0

  /** Total number of files in the gallery. */
  @property({ type: Number }) total = 0

  /** Whether the current file is included in the selected set. */
  @property({ type: Boolean }) isSelected = false

  /** Number of currently selected files (drives the download button label and enabled state). */
  @property({ type: Number }) selectedCount = 0

  /** Formatted total size of all selected files (e.g. "4.2 MB"). */
  @property({ type: String }) selectedSize = ''

  /** True while a ZIP download is in progress; disables the download button. */
  @property({ type: Boolean }) isDownloading = false

  /** Whether the view downloads only its active file. */
  @property({ type: Boolean }) singleFileDownload = false

  @state() private _hasPlayableMediaError = false

  @state() private _hasPlayableMediaLoaded = false

  private _playableMediaTimeoutId: number | undefined

  override connectedCallback(): void {
    super.connectedCallback()
    document.addEventListener('keydown', this._handleKeydown, true)
  }

  override disconnectedCallback(): void {
    document.removeEventListener('keydown', this._handleKeydown, true)
    this._clearPlayableMediaTimeout()
    super.disconnectedCallback()
  }

  protected override willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    if (changedProperties.has('file')) {
      this._clearPlayableMediaTimeout()
      this._hasPlayableMediaError = false
      this._hasPlayableMediaLoaded = false
    }
  }

  protected override updated(changedProperties: Map<PropertyKey, unknown>): void {
    if (!changedProperties.has('file') || !this.file) {
      return
    }

    if (this._isPlayableMedia(this.file)) {
      this._startPlayableMediaTimeout()
      this.renderRoot.querySelector<HTMLMediaElement>('video, audio')?.load()
    }
  }

  private _handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this._onClose()
    } else if (event.key === 'ArrowLeft') {
      this._onPrev()
    } else if (event.key === 'ArrowRight') {
      this._onNext()
    }
  }

  /** Emits `detail-close`. */
  private _onClose(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.DETAIL_CLOSE, { bubbles: true, composed: true }))
  }

  /** Emits `detail-prev`. */
  private _onPrev(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.DETAIL_PREV, { bubbles: true, composed: true }))
  }

  /** Emits `detail-next`. */
  private _onNext(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.DETAIL_NEXT, { bubbles: true, composed: true }))
  }

  /** Emits `file-select` with the current file ID. */
  private _onSelect(): void {
    if (!this.file) {
      return
    }
    this.dispatchEvent(
      new CustomEvent<string>(GalleryEvent.FILE_SELECT, {
        detail: this.file.id,
        bubbles: true,
        composed: true,
      }),
    )
  }

  /** Emits `download-request`. */
  private _onDownload(): void {
    if ((!this.singleFileDownload && this.selectedCount === 0) || this.isDownloading) {
      return
    }

    this.dispatchEvent(new CustomEvent(GalleryEvent.DOWNLOAD_REQUEST, { bubbles: true, composed: true }))
  }

  /** Emits `preview-load-error` with the current file ID. */
  private _onPreviewLoadError(): void {
    if (!this.file) {
      return
    }

    this.dispatchEvent(
      new CustomEvent<PreviewLoadErrorEventDetail>(GalleryEvent.PREVIEW_LOAD_ERROR, {
        detail: { id: this.file.id },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private _onPlayableMediaError = (): void => {
    this._clearPlayableMediaTimeout()
    this._hasPlayableMediaError = true
  }

  private _onPlayableMediaLoaded = (): void => {
    this._clearPlayableMediaTimeout()
    this._hasPlayableMediaLoaded = true
  }

  private _isPlayableMedia(file: FileModel): boolean {
    return file.contentType.startsWith('video/') || file.contentType.startsWith('audio/')
  }

  private _startPlayableMediaTimeout(): void {
    this._clearPlayableMediaTimeout()
    this._playableMediaTimeoutId = window.setTimeout(this._onPlayableMediaError, PLAYABLE_MEDIA_TIMEOUT_MS)
  }

  private _clearPlayableMediaTimeout(): void {
    if (this._playableMediaTimeoutId !== undefined) {
      window.clearTimeout(this._playableMediaTimeoutId)
      this._playableMediaTimeoutId = undefined
    }
  }

  private _renderImage(file: FileModel) {
    return html`<img
      class="preview-img"
      src="${file.thumbnailUrl}"
      alt="${file.name}"
      @error=${this._onPreviewLoadError}
    />`
  }

  private _renderVideo(file: FileModel) {
    return html`
      <video
        controls
        preload="metadata"
        aria-label="${file.name}"
        class=${this._hasPlayableMediaLoaded ? 'preview-video' : 'preview-video preview-media-loading'}
        @loadedmetadata=${this._onPlayableMediaLoaded}
        @error=${this._onPlayableMediaError}
      >
        <source
          src="${file.contentUrl}"
          type=${file.contentType === 'video/quicktime' ? nothing : file.contentType}
          @error=${this._onPlayableMediaError}
        />
      </video>
    `
  }

  private _renderAudio(file: FileModel) {
    return html`
      <audio
        controls
        preload="metadata"
        aria-label="${file.name}"
        class=${this._hasPlayableMediaLoaded ? 'preview-audio' : 'preview-audio preview-media-loading'}
        @loadedmetadata=${this._onPlayableMediaLoaded}
        @error=${this._onPlayableMediaError}
      >
        <source src="${file.contentUrl}" type="${file.contentType}" @error=${this._onPlayableMediaError} />
      </audio>
    `
  }

  private _renderPreview(file: FileModel) {
    const previewMode = getPreviewMode(file, this._hasPlayableMediaError)

    switch (previewMode) {
      case 'image':
        return this._renderImage(file)
      case 'video':
        return this._renderVideo(file)
      case 'audio':
        return this._renderAudio(file)
      case 'highlighted-icon':
        return renderPreviewIcon(file.contentType, false, true)
      case 'neutral-icon':
        return renderPreviewIcon(file.contentType)
    }
  }

  private _renderHeader(file: FileModel) {
    return html`<div class="details-header">
      <div class="header-info">
        <h2 class="header-title">${file.name}</h2>
        <span class="header-type">${contentTypeToLabel(file.contentType)}</span>
      </div>
      <chameleon-icon-button
        id="close-button"
        size="large"
        variant="neutral-tertiary"
        aria-label="${t('gallery.actions.close', 'Close')}"
        @click=${this._onClose}
      >
        <chameleon-svg>${unsafeSVG(SVG_CLOSE)}</chameleon-svg>
      </chameleon-icon-button>
      <chameleon-tooltip trigger-id="close-button">${t('gallery.actions.close', 'Close')}</chameleon-tooltip>
    </div>`
  }

  private _renderSelection() {
    return this.singleFileDownload
      ? nothing
      : html`<div class="select-bar">
          <chameleon-checkbox .checked=${this.isSelected} @change=${this._onSelect}
            >${t('gallery.detail.select', 'Select')}</chameleon-checkbox
          >
        </div>`
  }

  private _renderNavigation() {
    const isPrevDisabled = this.index === 0
    const isNextDisabled = this.index >= this.total - 1

    return html`<div class="nav-bar">
      <chameleon-icon-button
        size="medium"
        variant="neutral-tertiary"
        ?disabled=${isPrevDisabled}
        aria-label="${t('gallery.navigation.previousFile', 'Previous file')}"
        @click=${this._onPrev}
      >
        <chameleon-svg>${unsafeSVG(SVG_CHEVRON_LEFT)}</chameleon-svg>
      </chameleon-icon-button>
      <span class="nav-counter"
        >${t('gallery.detail.fileCount', '{{index}} of {{total}} files', {
          index: this.index + 1,
          total: this.total,
        })}</span
      >
      <chameleon-icon-button
        size="medium"
        variant="neutral-tertiary"
        ?disabled=${isNextDisabled}
        aria-label="${t('gallery.navigation.nextFile', 'Next file')}"
        @click=${this._onNext}
      >
        <chameleon-svg>${unsafeSVG(SVG_CHEVRON_RIGHT)}</chameleon-svg>
      </chameleon-icon-button>
    </div>`
  }

  private _renderDownload() {
    const downloadDisabled = (!this.singleFileDownload && this.selectedCount === 0) || this.isDownloading

    return html`<div class="bottom-bar">
      <chameleon-button
        variant="brand-primary"
        size="large"
        ?is-loading=${this.isDownloading}
        ?disabled=${downloadDisabled}
        @click=${this._onDownload}
      >
        <chameleon-svg slot="start" style="--goto-icon-size: 20px">${unsafeSVG(SVG_DOWNLOAD)}</chameleon-svg>
        ${this.singleFileDownload
          ? t('gallery.actions.downloadFile', 'Download file')
          : t('gallery.actions.downloadFiles', 'Download files ({{fileCount}})', {
              fileCount: this.selectedCount,
            })}
      </chameleon-button>
    </div>`
  }

  override render() {
    if (!this.file) {
      return html``
    }

    return html`
      ${this._renderHeader(this.file)} ${this._renderSelection()}
      <div class="preview-area">${this._renderPreview(this.file)}</div>
      ${this._renderNavigation()} ${this._renderDownload()}
    `
  }
}
